import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type MailOutboxView, type MailStatus } from '../api/client.js';
import { useSession } from '../auth/useSession.js';
import { Alert, Button, Card, Input, PageHeader, Spinner, TableScroll } from '../components/ui.js';
import { dateTime } from '../format.js';

/**
 * Outgoing mail, for administrators: whether a provider is configured, the
 * outbox counts, a test that reports its own result, and the recent messages
 * with a retry for anything that failed.
 *
 * Messages here are account mail (invitations and password resets) and
 * operational alerts. With no provider configured a message is held and goes out
 * once one is — and an invitation or reset link is handed to the administrator on
 * the Users page instead, so accounts can still be set up by hand. The list shows
 * only the subject; a message's body (which for account mail is the link itself)
 * is never in the preview.
 */

const KINDS: Record<string, string> = {
  INVITATION: 'Invitation',
  PASSWORD_RESET: 'Password reset',
  SPAPI_FETCH_FAILED: 'SP-API fetch failed',
  SPAPI_CYCLE_FAILED: 'SP-API cycle failed',
  SPAPI_CONNECTION: 'SP-API connection',
  OFFERS_DIGEST: 'Offers signal',
  LISTINGS_GUARD: 'Listings held back',
  TEST: 'Test',
};

function kindLabel(kind: string): string {
  return KINDS[kind] ?? kind;
}

function statusText(m: MailOutboxView): string {
  switch (m.status) {
    case 'sent':
      return `Sent ${dateTime(m.sentAt)}`;
    case 'failed':
      return `Failed after ${m.attempts} attempt${m.attempts === 1 ? '' : 's'}`;
    case 'held':
      return 'Held — waiting on a mail provider';
    case 'queued':
      return `Queued, attempt ${m.attempts + 1} at ${dateTime(m.nextAttemptAt)}`;
  }
}

export function EmailPage() {
  const session = useSession();
  const ownEmail = session.status === 'signedIn' ? session.user.email : '';
  const queryClient = useQueryClient();
  const [to, setTo] = useState(ownEmail);
  const [result, setResult] = useState<MailOutboxView | null>(null);
  const [error, setError] = useState<string | null>(null);

  const status = useQuery({
    queryKey: ['mail-status'],
    queryFn: () => api.get<MailStatus>('/api/mail/status'),
  });

  const test = useMutation({
    mutationFn: (address: string) =>
      api.post<{ message: MailOutboxView }>('/api/mail/test', address ? { to: address } : {}),
    onSuccess: async ({ message }) => {
      setError(null);
      setResult(message);
      await queryClient.invalidateQueries({ queryKey: ['mail-status'] });
    },
    onError: (caught: unknown) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not send the test.'),
  });

  const retry = useMutation({
    mutationFn: (id: string) => api.post<{ status: string }>(`/api/mail/${id}/retry`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mail-status'] }),
    onError: (caught: unknown) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not retry the message.'),
  });

  const s = status.data;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Email"
        description="Outgoing mail: whether it is configured, what has been sent, and anything that failed."
      />

      {error && <Alert>{error}</Alert>}
      {status.isPending && <Spinner label="Checking mail" />}
      {status.isError && <Alert>{status.error.message}</Alert>}

      {s && !s.configured && (
        <Alert tone="warning">
          Outgoing mail is not configured on this server. Alerts are recorded here and held, and go
          out once a provider is set up. Invitations and password resets are not held — their links
          expire — so they are shown to the administrator on the Users page to pass on by hand
          instead. Set MAIL_PROVIDER in the environment; the steps are in docs/email-setup.md.
        </Alert>
      )}

      {s && (
        <Card className="space-y-3 p-5">
          <div className="flex flex-wrap items-center gap-3">
            <div>
              <h2 className="text-sm font-medium text-body">
                {s.configured ? `Sending as ${s.from.name} <${s.from.address}>` : 'Not configured'}
              </h2>
              <p className="text-xs text-muted">
                Provider: {s.provider ?? 'none'} · {s.counts.sent} sent · {s.counts.queued} queued ·{' '}
                {s.counts.held} held · {s.counts.failed} failed
              </p>
            </div>
            <form
              className="ml-auto flex items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                test.mutate(to);
              }}
            >
              <Input
                type="email"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder={ownEmail}
                className="w-64"
                aria-label="Send a test message to"
              />
              <Button type="submit" variant="secondary" disabled={test.isPending}>
                {test.isPending ? 'Sending…' : 'Send a test'}
              </Button>
            </form>
          </div>
          {result && (
            <Alert tone={result.status === 'sent' ? 'success' : 'info'}>
              {result.status === 'sent'
                ? `Test message sent to ${result.toEmail}.`
                : result.status === 'held'
                  ? `Test message to ${result.toEmail} is held: no mail provider is configured.`
                  : `Test message to ${result.toEmail} ${
                      result.status === 'failed' ? 'failed' : 'is queued'
                    }${result.lastError ? `: ${result.lastError}` : '.'}`}
            </Alert>
          )}
        </Card>
      )}

      {s && (
        <Card>
          <TableScroll>
            <table className="w-full text-sm">
              <thead className="border-b border-line text-left text-xs text-muted">
                <tr>
                  <th className="px-4 py-2 font-medium">When</th>
                  <th className="px-4 py-2 font-medium">Kind</th>
                  <th className="px-4 py-2 font-medium">To</th>
                  <th className="px-4 py-2 font-medium">Subject</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {s.recent.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-muted">
                      Nothing has been sent yet.
                    </td>
                  </tr>
                )}
                {s.recent.map((m) => (
                  <tr key={m.id}>
                    <td className="whitespace-nowrap px-4 py-2 text-muted">
                      {dateTime(m.createdAt)}
                    </td>
                    <td className="px-4 py-2 text-body">{kindLabel(m.kind)}</td>
                    <td className="px-4 py-2 text-body">{m.toEmail}</td>
                    <td className="px-4 py-2 text-body">{m.subject}</td>
                    <td className="px-4 py-2">
                      <span
                        className={
                          m.status === 'sent'
                            ? 'text-body'
                            : m.status === 'failed'
                              ? 'text-danger-text'
                              : 'text-warn-text'
                        }
                      >
                        {statusText(m)}
                      </span>
                      {m.lastError && m.status !== 'sent' && (
                        <div
                          className="mt-0.5 max-w-md truncate text-xs text-muted"
                          title={m.lastError}
                        >
                          {m.lastError}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {m.status === 'failed' && (
                        <Button variant="ghost" onClick={() => retry.mutate(m.id)}>
                          Retry
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        </Card>
      )}
    </div>
  );
}
